import { AVAILABILITY_ZONE, AvailabilityZones } from 'constants/database';
import { Provider } from 'constants/provider';
import { RESOURCES_NAME_LENGTH_LIMIT } from 'constants/resources-name-length-limit';
import { ServerService } from 'constants/service';
import {
  DatabaseSpecWithIdV2,
  DeploymentRegionsV2Response,
  DeploymentServiceGroupsV2Response,
  DeploymentTypesV2Response,
} from 'sync/configuration-service';
import { validateString } from 'utils/validators';
import { ProvisionedForm } from './create-database.types';

export function parseSpecsToCreatePayload(specs: DatabaseSpecWithIdV2[]) {
  return specs.map(({ key, compute, count, disk, diskAutoScaling, services }) => ({
    key,
    compute,
    storage: {
      key: disk.type,
      sizeInGb: disk.sizeInGb,
      iops: disk.iops,
      ...(diskAutoScaling?.enabled !== undefined ? { autoScalingEnabled: diskAutoScaling.enabled } : {}),
    },
    services,
    nodes: count,
  }));
}

export function parseSpecsToDbCostPayload(specs: DatabaseSpecWithIdV2[]) {
  return specs.map((spec) => ({
    compute: spec.compute,
    disk: {
      type: spec.disk.type,
      sizeInGb: spec.disk.sizeInGb,
      iops: spec.disk.iops,
    },
    services: spec.services,
    count: spec.count,
  }));
}

export function specsResponseToServiceGroup(
  id: number,
  serviceGroup: DeploymentServiceGroupsV2Response['serviceGroupsTemplates'][number]['serviceGroups'][number],
  template: DeploymentServiceGroupsV2Response['serviceGroupsTemplates'][number],
  provider: Provider,
  services: Partial<ServerService>[],
  computeOptions: DeploymentServiceGroupsV2Response['compute'],
  diskOptions: DeploymentServiceGroupsV2Response['storage'],
  storageAutoExpansion?: DeploymentRegionsV2Response['storageAutoExpansion']
) {
  const storage = diskOptions.find((s) => s.key === template.storage.defaultOption);
  return {
    id,
    key: serviceGroup.key,
    totalNodes: template.nodes,
    nodes: serviceGroup.nodes,
    provider,
    count: serviceGroup.nodes.min,
    services,
    serviceOptions: serviceGroup.services,
    compute: template.compute.defaultOption,
    computeOptions: computeOptions
      .filter((c) => template.compute.options.includes(c.key))
      .map(({ cpus, key, memoryInGb }) => ({ key, vcpus: `${cpus}vCPUs`, memory: `${memoryInGb}GB` })),
    disk: {
      type: template.storage.defaultOption,
      sizeInGb: storage!.size[0].min,
      iops: storage?.size[0].iops?.min,
    },
    restrictedToSizesInGb: template.storage.restrictedToSizesInGb,
    diskOptions: diskOptions.filter((option) => template.storage.options.includes(option.key)),
    ...(storageAutoExpansion?.enabled
      ? {
          diskAutoScaling: {
            enabled: Boolean(storageAutoExpansion.default),
          },
        }
      : {}),
  };
}

export function getServerVersionOptions(options?: DeploymentTypesV2Response['serverVersions']['options']) {
  return (
    options?.map(({ key, description, url }) => {
      return { value: key, label: key, description, linkHref: url, linkText: 'Learn More' };
    }) || []
  );
}

export function getTemplateOptions(options?: DeploymentServiceGroupsV2Response['serviceGroupsTemplates']) {
  return (
    options?.map(({ key, displayName, description }) => ({
      label: displayName,
      value: key,
      description,
    })) || []
  );
}

export function getAvailabilityZonesOptions(
  planAndTimeZone: ProvisionedForm['planAndTimeZone'],
  availabilityZones?: DeploymentServiceGroupsV2Response['availabilityZones']
) {
  let parsedZones = availabilityZones?.eligibility.enabled
    ? availabilityZones?.options.map(({ key, restrictedToPlans }) => {
        return {
          label: AVAILABILITY_ZONE[key],
          value: key,
          disabled:
            (restrictedToPlans && !restrictedToPlans.includes(planAndTimeZone!.plan)) ||
            (AVAILABILITY_ZONE[key] === 'Multiple' && planAndTimeZone!.plan === 'Basic'),
          tooltip:
            AVAILABILITY_ZONE[key] === 'Multiple' && planAndTimeZone!.plan === 'Basic'
              ? 'For Basic plan only Single AZ option is available, please select Developer Pro option for Multiple AZs'
              : '',
        };
      }) || []
    : [];

  if (availabilityZones?.options.length === 1) {
    parsedZones = [
      ...parsedZones,
      {
        label: 'Multiple',
        value: 'multi',
        disabled: true,
        tooltip: '',
      },
    ];
  }
  return parsedZones;
}

export function getZonesDefaults(options?: DeploymentServiceGroupsV2Response['availabilityZones']['options']) {
  return options?.reduce<{ [P in AvailabilityZones]?: Array<string> }>((acc, value) => {
    acc[value.key] = value.zonesSelection.default;
    return acc;
  }, {});
}

export function validateDatabaseName(name: string) {
  return validateString({
    required: true,
    minLength: 2,
    maxLength: RESOURCES_NAME_LENGTH_LIMIT.provisionedDatabase,
    canNotStartWith: [' '],
    allowedChars: ['letters', 'numbers', 'dashes', 'underscores', 'spaces'],
  })(name).length;
}

export function validateCidrBlock(cidr: string, blacklistedBlocks?: string[]) {
  return validateString({
    required: true,
    inUseValuesList: blacklistedBlocks,
  })(cidr).length;
}
